// deps
import React, { memo, useMemo } from "react";

// internal deps
import { TransparentLineV2, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// types
import { PLBillDetailsData, PLBillLineItem } from "@swiggy-private/cart-types";
import { PLBillCtaAction, PlanDetails } from "@swiggy-private/cart-types";

// components
import { BillSeparator } from "@swiggy-private/cart-ui";
import { BillDisplayItem } from "@swiggy-private/cart-ui";
import BillTooltipItem from "./components/bill-tooltip-item/BillTooltipItem";
import BillBannerItem from "./components/bill-banner-item/BillBannerItem";
import PLBillCollapsibleHeader from "./components/pl-bill-collapsible-header/PLBillCollapsibleHeader";

// hooks
import usePLBillActions from "@cart-client/hooks/usePLBillActions";

// styles
import { BillDetailCartGroupContainer } from "./styles";
import { VALID_PL_BILL_DETAILS_TYPES } from "@swiggy-private/cart-ui";
import { PL_BILL_DETAILS_RENDERER_TEST_IDS } from "@swiggy-private/cart-test-ids";

import { recordPLBillDetailsDataPresent } from "./PLBillDetailsNREvents";

export interface PLBillDetailsRendererProps {
    details: PLBillDetailsData;
    setBillDetailsOneCardComms?: VoidFunction;
    planDetails?: PlanDetails;
}

const renderLineItem = (item: PLBillLineItem, idx: number, actionMap: Record<PLBillCtaAction, VoidFunction>) => {
    switch (item.type) {
        case VALID_PL_BILL_DETAILS_TYPES.SEPARATOR:
            return <BillSeparator key={idx} />;

        case VALID_PL_BILL_DETAILS_TYPES.DISPLAY:
            return <BillDisplayItem key={idx} item={item} actionMap={actionMap} />;

        case VALID_PL_BILL_DETAILS_TYPES.TOOLTIP:
            return <BillTooltipItem key={idx} item={item} actionMap={actionMap} />;

        case VALID_PL_BILL_DETAILS_TYPES.BANNER:
            return <BillBannerItem key={idx} item={item} />;

        default:
            return null;
    }
};

const PLBillDetailsRenderer = ({ details, setBillDetailsOneCardComms, planDetails }: PLBillDetailsRendererProps) => {
    const header = useMemo(() => details.header[0], [details.header]);
    const actionMap = usePLBillActions({ setBillDetailsOneCardComms, planDetails });

    const { line_items: lineItems } = details;

    useEffectOnce(() => {
        recordPLBillDetailsDataPresent({
            hasCollapsedData: !isEmpty(details.header),
            hasUncollapsedData: !isEmpty(lineItems),
        });
    });

    return (
        <>
            <TransparentLineV2 />
            <div id="bill-details-container" data-testid={PL_BILL_DETAILS_RENDERER_TEST_IDS.CONTAINER}>
                <BillDetailCartGroupContainer isCollapsible>
                    <PLBillCollapsibleHeader header={header}>
                        {lineItems.map((item, idx) => renderLineItem(item, idx, actionMap))}
                    </PLBillCollapsibleHeader>
                </BillDetailCartGroupContainer>
            </div>
        </>
    );
};

export default memo(PLBillDetailsRenderer);
